import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Crown, Users, Copy, Trash2, Heart, Shield, UserPlus, Check,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageLoader, Modal, Btn, EmptyState, ConfirmButton } from "@/components/common";

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [assignOpen, setAssignOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data: camp, isLoading } = useQuery({
    queryKey: ["campaign", id],
    queryFn: async () => (await api.get(`/campaigns/${id}`)).data,
  });
  const { data: myChars } = useQuery({
    queryKey: ["characters"],
    queryFn: async () => (await api.get("/characters")).data,
  });

  const assign = useMutation({
    mutationFn: async (charId) => (await api.post(`/campaigns/${id}/assign/${charId}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["campaign", id] }); setAssignOpen(false); toast.success("Character added to party!"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async () => (await api.delete(`/campaigns/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["campaigns"] }); toast.success("Campaign deleted"); navigate("/campaigns"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (isLoading || !camp) return <PageLoader />;

  const copyCode = () => {
    navigator.clipboard.writeText(camp.invite_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
    toast.success("Invite code copied!");
  };

  const unassigned = (myChars || []).filter((c) => c.campaign_id !== id);

  return (
    <div className="animate-float-up">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate("/campaigns")} className="p-2 rounded-xl glass glass-hover"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <h1 className="font-display text-2xl font-bold text-white flex items-center gap-2">
            {camp.name}
            {camp.is_dm && <span className="flex items-center gap-1 text-xs text-amber-300 bg-amber-500/15 px-2.5 py-1 rounded-full"><Crown size={12} /> DM</span>}
          </h1>
          {camp.setting && <p className="text-sm text-violet-300/70">{camp.setting}</p>}
        </div>
        {camp.is_dm && (
          <ConfirmButton onConfirm={() => del.mutate()} title="Delete campaign?" message="This permanently deletes the campaign for all players." data-testid="delete-campaign"
            className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition bg-rose-500/15 text-rose-300 border border-rose-500/30 hover:bg-rose-500/25">
            <Trash2 size={16} /> Delete
          </ConfirmButton>
        )}
      </div>

      {camp.description && <p className="text-zinc-400 mb-6 glass rounded-2xl p-4">{camp.description}</p>}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl text-white">Party · {camp.characters?.length || 0}</h2>
            <Btn variant="ghost" onClick={() => setAssignOpen(true)} data-testid="add-to-party-btn"><UserPlus size={16} /> Add my character</Btn>
          </div>
          {camp.characters?.length ? (
            <div className="grid sm:grid-cols-2 gap-3">
              {camp.characters.map((c) => (
                <Link key={c.id} to={`/characters/${c.id}`} className="glass glass-hover rounded-2xl p-4 flex items-center gap-3" data-testid={`party-char-${c.id}`}>
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-600 to-violet-900 flex items-center justify-center font-display font-bold">{c.name?.[0]?.toUpperCase()}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-white truncate">{c.name}</div>
                    <div className="text-xs text-zinc-500">Lvl {c.level} {c.class}</div>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="flex items-center gap-1 text-rose-400"><Heart size={12} /> {c.hp?.current}/{c.hp?.max}</span>
                    <span className="flex items-center gap-1 text-sky-400"><Shield size={12} /> {c.armor_class}</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState icon={Users} title="No party members" subtitle="Add your characters or share the invite code with players." />
          )}
        </div>

        <div className="space-y-4">
          <div className="glass rounded-3xl p-5">
            <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2">Invite Code</div>
            <button onClick={copyCode} data-testid="invite-code" className="w-full flex items-center justify-between gap-2 glass-hover rounded-xl px-4 py-3 border border-white/10">
              <span className="font-mono text-xl tracking-[0.3em] text-violet-300">{camp.invite_code}</span>
              {copied ? <Check size={18} className="text-teal-400" /> : <Copy size={18} className="text-zinc-500" />}
            </button>
            <p className="text-xs text-zinc-600 mt-2">Share this code so players can join.</p>
          </div>

          <div className="glass rounded-3xl p-5">
            <div className="text-xs uppercase tracking-wide text-zinc-500 mb-3">Members · {camp.members?.length || 0}</div>
            <div className="space-y-2">
              {camp.members?.map((m) => (
                <div key={m.id} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-violet-500/15 flex items-center justify-center text-xs font-bold text-violet-300">{m.username?.[0]?.toUpperCase()}</div>
                  <span className="text-sm text-zinc-300">{m.username}</span>
                  {m.id === camp.dm_id && <Crown size={12} className="text-amber-400 ml-auto" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Modal open={assignOpen} onClose={() => setAssignOpen(false)} title="Add a character to this party">
        {unassigned.length ? (
          <div className="space-y-2">
            {unassigned.map((c) => (
              <button key={c.id} onClick={() => assign.mutate(c.id)} data-testid={`assign-${c.id}`} className="w-full glass glass-hover rounded-2xl p-3 flex items-center gap-3 text-left">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-violet-900 flex items-center justify-center font-display font-bold">{c.name?.[0]?.toUpperCase()}</div>
                <div className="flex-1"><div className="font-semibold text-white">{c.name}</div><div className="text-xs text-zinc-500">Lvl {c.level} {c.class}</div></div>
                <UserPlus size={16} className="text-violet-300" />
              </button>
            ))}
          </div>
        ) : <p className="text-sm text-zinc-500 text-center py-6">All your characters are already assigned, or you have none yet.</p>}
      </Modal>
    </div>
  );
}
